import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import RestoreFromTrashOutlinedIcon from '@mui/icons-material/RestoreFromTrashOutlined';
import OfflinePinOutlinedIcon from '@mui/icons-material/OfflinePinOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import CableOutlinedIcon from '@mui/icons-material/CableOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { Perm } from '@/auth/permissions';
import { Modules } from '@/core/modules/moduleKeys';
import { accountingNavGroup } from '@/modules/accounting/nav';
import { hrNavGroup } from '@/modules/hr/nav';
import { fleetNavGroup } from '@/modules/fleet/nav';
import { parkingNavGroup } from '@/modules/parking/nav';
import { propertyNavGroup } from '@/modules/property/nav';
import { lendingNavGroup } from '@/modules/lending/nav';
import { learningNavGroup } from '@/modules/learning/nav';
import { hospitalNavGroup } from '@/modules/hospital/nav';
import { budgetingNavGroup } from '@/modules/budgeting/nav';

export interface NavItem {
  label: string;
  path: string;
  icon: SvgIconComponent;
  /** Permission required to see and open this item (undefined = any authenticated user). */
  permission?: string;
  /** Any-of permissions: item is visible when the user holds at least one of these. */
  anyPermission?: string[];
  /** When true the item is a placeholder for a module delivered in a later milestone. */
  comingSoon?: boolean;
  /** The platform module the item belongs to; hidden unless the organisation has the module on. */
  module?: string;
  /** A fact about the organisation's set-up the item needs, reported by its module (e.g. `property.airport`); hidden until known to be true. */
  feature?: string;
}

/** A collapsible heading in the sidebar with the entries that belong together under it, which may themselves be groups. */
export interface NavGroup {
  kind: 'group';
  /** Stable key the open / closed state is remembered by. */
  key: string;
  label: string;
  icon: SvgIconComponent;
  items: NavEntry[];
}

export type NavEntry = NavItem | NavGroup;

export const isNavGroup = (entry: NavEntry): entry is NavGroup => (entry as NavGroup).kind === 'group';

/**
 * Primary navigation for Annova ERP: a few items at the top level, the rest in collapsible groups
 * of related pages. Each item declares the permission (and, for a module's pages, the module)
 * needed to see it; the sidebar hides items the person is not authorised for and a group whose
 * items are all hidden.
 */
export const navigation: NavEntry[] = [
  // No permission: the home page shows what is the person's own.
  { label: 'Home', path: '/', icon: HomeOutlinedIcon },
  { label: 'Dashboards', path: '/dashboards', icon: DashboardOutlinedIcon, permission: Perm.Dashboard.View },
  { label: 'Analytics', path: '/analytics', icon: InsightsOutlinedIcon, permission: Perm.Analytics.View },
  {
    kind: 'group', key: 'strategy', label: 'Strategy & Performance', icon: AccountTreeOutlinedIcon,
    items: [
      { label: 'Strategy', path: '/strategy', icon: AccountTreeOutlinedIcon, permission: Perm.Strategy.View },
      { label: 'Strategy Framework', path: '/strategy-framework', icon: SchemaOutlinedIcon, permission: Perm.Strategy.View },
      { label: 'KPIs', path: '/kpis', icon: TrackChangesOutlinedIcon, permission: Perm.Kpis.View },
      { label: 'Initiatives & Projects', path: '/initiatives', icon: RocketLaunchOutlinedIcon, permission: Perm.Initiatives.View },
      { label: 'Risks', path: '/risks', icon: GppMaybeOutlinedIcon, permission: Perm.Risks.View },
    ],
  },
  {
    kind: 'group', key: 'governance', label: 'Governance', icon: GavelOutlinedIcon,
    items: [
      { label: 'Meetings', path: '/reviews', icon: EventNoteOutlinedIcon, permission: Perm.Governance.View },
      { label: 'Calendar', path: '/calendar', icon: CalendarMonthOutlinedIcon, permission: Perm.Governance.View },
      { label: 'Board Packs', path: '/board-packs', icon: LibraryBooksOutlinedIcon, permission: Perm.BoardPacks.View },
      // No permission: every user sees the packs issued to them, and nothing else.
      { label: 'My Board Packs', path: '/my-board-packs', icon: InboxOutlinedIcon },
      { label: 'Decisions', path: '/decisions', icon: GavelOutlinedIcon, permission: Perm.Decisions.View },
      { label: 'Actions', path: '/actions', icon: PlaylistAddCheckOutlinedIcon, permission: Perm.Actions.View },
      { label: 'Assessments', path: '/questionnaires', icon: FactCheckOutlinedIcon, permission: Perm.Governance.View },
    ],
  },
  {
    kind: 'group', key: 'workspace', label: 'Workspace', icon: WorkspacesOutlinedIcon,
    items: [
      // Every approval decided here — KPI reviews, journals, HR requests and payroll alike; the same rights as the page's own guard.
      { label: 'Approvals', path: '/approvals', icon: AssignmentTurnedInOutlinedIcon, anyPermission: [Perm.Kpis.Review, Perm.Accounting.Post, Perm.Hr.Approve, Perm.Hr.ApprovePayroll, Perm.Fleet.Approve, Perm.Parking.Approve, Perm.Property.ApproveLeasing, Perm.Learning.Approve] },
      // No permission: anyone may have something to sign; the module must be on (docs §219).
      { label: 'Signatures', path: '/signing', icon: DrawOutlinedIcon, module: Modules.Signing },
      // No permission: a review note that named you is yours to read, whatever else you may see.
      { label: 'Mentions', path: '/mentions', icon: AlternateEmailIcon },
      // No permission: Annova Me, the person's own HR record, where HR has linked one to their login.
      { label: 'Annova Me', path: '/me', icon: BadgeOutlinedIcon, module: Modules.Hr },
      // No permission: every member of an organisation with Learning on may learn (docs §266).
      { label: 'My learning', path: '/learn', icon: SchoolOutlinedIcon, module: Modules.Learning },
      { label: 'Reports', path: '/reports', icon: DescriptionOutlinedIcon, permission: Perm.Reports.View },
    ],
  },
  {
    // Documents: the repository and everything around it, a menu of its own (no longer under Workspace).
    kind: 'group', key: 'documents', label: 'Documents', icon: FolderCopyOutlinedIcon,
    items: [
      { label: 'Repository', path: '/evidence', icon: FolderCopyOutlinedIcon, permission: Perm.Evidence.View },
      // Deleted documents waiting to be purged (docs §257); restorable until then.
      { label: 'Recycle bin', path: '/evidence/recycle-bin', icon: RestoreFromTrashOutlinedIcon, permission: Perm.Evidence.View },
      // The document administrators' dashboard (docs §260).
      { label: 'Dashboard', path: '/evidence/dashboard', icon: SpaceDashboardOutlinedIcon, anyPermission: [Perm.Evidence.Manage, Perm.Documents.Admin, Perm.Documents.Records] },
      // Documents kept on this device to read offline (docs §259).
      { label: 'Offline documents', path: '/evidence/offline', icon: OfflinePinOutlinedIcon, permission: Perm.Evidence.View },
      // The capture desk (docs §252): scanning batches into the repository, where Document Management is on.
      { label: 'Capture', path: '/documents/capture', icon: DocumentScannerOutlinedIcon, module: Modules.Documents, anyPermission: [Perm.Documents.Capture, Perm.Documents.Admin, Perm.Evidence.Manage] },
      // The compliance register (docs §256): documents whose validity runs out, and their reminders.
      { label: 'Compliance', path: '/documents/compliance', icon: EventAvailableOutlinedIcon, module: Modules.Documents, anyPermission: [Perm.Documents.Compliance, Perm.Documents.Admin, Perm.Evidence.Manage] },
    ],
  },
  // Optional modules: each item names its module and appears only where the organisation has it on.
  // The accounting module: one collapsible menu, its ledgers as collapsible sub-menus.
  // Budgeting sits inside the Accounting menu, above Accounting Settings (docs §206).
  { ...accountingNavGroup, items: [...accountingNavGroup.items.filter((e) => !(isNavGroup(e) && e.key === 'accounting-settings')), budgetingNavGroup, ...accountingNavGroup.items.filter((e) => isNavGroup(e) && e.key === 'accounting-settings')] },
  // The HR module: its own menu rather than a sub-menu of Accounting, because payroll is only one
  // part of it and the rest (people, leave, recruitment) is nobody's idea of a ledger.
  hrNavGroup,
  fleetNavGroup,
  parkingNavGroup,
  propertyNavGroup,
  lendingNavGroup,
  learningNavGroup,
  hospitalNavGroup,
  {
    kind: 'group', key: 'administration', label: 'Administration', icon: AdminPanelSettingsOutlinedIcon,
    items: [
      { label: 'Administration', path: '/administration', icon: SettingsOutlinedIcon, anyPermission: [Perm.Users.View, Perm.Roles.View, Perm.Settings.Manage, Perm.Platform.Manage, Perm.Risks.Configure] },
      { label: 'Workflows', path: '/workflows', icon: HubOutlinedIcon, permission: Perm.Settings.Manage },
      { label: 'Integrations', path: '/integrations', icon: CableOutlinedIcon, permission: Perm.Settings.Manage },
      { label: 'API Keys', path: '/api-keys', icon: VpnKeyOutlinedIcon, permission: Perm.Settings.Manage },
      { label: 'Webhooks', path: '/webhooks', icon: CableOutlinedIcon, permission: Perm.Settings.Manage },
      { label: 'Audit Trail', path: '/audit', icon: HistoryOutlinedIcon, permission: Perm.Audit.View },
      { label: 'Data protection', path: '/privacy', icon: PrivacyTipOutlinedIcon, anyPermission: [Perm.Privacy.View, Perm.Privacy.Manage] },
      { label: 'System Health', path: '/health', icon: MonitorHeartOutlinedIcon },
    ],
  },
];

/** Every item beneath an entry, however deep. */
export const flattenNav = (entries: readonly NavEntry[]): NavItem[] =>
  entries.flatMap((entry) => (isNavGroup(entry) ? flattenNav(entry.items) : [entry]));

/** Every item, flattened, for anything that needs the plain list (breadcrumbs, searches). */
export const navItems: NavItem[] = flattenNav(navigation);
